import Icon from '@/components/_rebrand/Icon';
import Modal from '@/components/_rebrand/Modal';
import { Text } from '@/components/_rebrand/Text';
import { useModalButtonActions } from '@/hooks/useModalButtonActions';
import cn from 'classnames';
import Link from 'next/link';
import { ButtonBaseClasses } from '../Button';
import { TertiaryButton } from '../Button/TertiaryButton';
import { IconName } from '../Icon/Icon.registry';
import { TextCombination } from '../TextCombination';
import ThemeWrapper from '../ThemeWrapper';

export type PricingModalProps = {
  onClose: () => void;
  name: string;
  content: {
    title: string;
    description: string;
    ctaLink: {
      title: string;
      href: string;
    };
    pricingTiers: {
      iconName: IconName;
      title: string;
      price: string;
      description: string;
    }[];
  }
}

export const PricingModal = ({
  onClose,
  name,
  content,
}: PricingModalProps) => {
  const { title, description, ctaLink, pricingTiers } = content;

  const { modalActionHandler } = useModalButtonActions();

  const handleFooterClick = () => {
    modalActionHandler('demo');
  };

  return (
    <Modal name={name} onClose={onClose}
      className="md:w-dimensions-840 p-0">
      <ThemeWrapper theme="theme-white">
        <div className="pt-xl pr-4xl pb-lg pl-xl space-y-md ">
          <TextCombination type="h3p2" align="left" title={title} description={description} />
          <Link href={ctaLink.href}>
            <TertiaryButton
              className={cn(ButtonBaseClasses, "flex items-center justify-center button-text-inverted space-x-xs")}
            >
              {ctaLink.title}
            </TertiaryButton>
          </Link>
        </div>
        <div className="border-l0 w-full h-[1px]" />
        <div className="flex flex-col px-xl">
          {pricingTiers.map((tier) => (
            <div key={tier.title} className="group pt-md first:pt-xl last:pb-lg">
              <div >
                <div className="flex flex-col md:flex-row justify-between gap-y-md">
                  <span>
                    <div className="flex flex-col md:flex-row md:items-center gap-xs">
                      <Icon name={tier.iconName} size={20} />
                      <Text type="p2-medium" className="text-primary">{tier.title}</Text>
                    </div>
                    <Text type="p2" className="text-secondary md:pl-xl">{tier.description}</Text>
                  </span>
                  <div className="my-auto flex items-center gap-xs">
                    <Text type="h3" className="text-primary">{tier.price}</Text>
                    <Text type="p2" className="text-secondary">/ conversation</Text>
                  </div>
                </div>
              </div>
              {/* Add a border-l0 to all but last tier */}
              <div className="border-l0 w-full h-[1px] mt-md group-last:hidden" />
            </div>
          ))}
        </div>
        {/* Disclaimer footer is hard-coded due to complexity of the inline demo modal link */}
        <div className="p-xs">
          <div className="flex flex-col md:flex-row bg-blue-100 border border-blue-200 border-solid rounded-8 p-s gap-xs">
            <Icon className='mt-[2px]' name="info-circle" size={16} />
            <Text type="caption1" className="text-primary">
              Pricing is pay-as-you-go, invoiced monthly based on your actual usage.
              <br />
              Volume discounts are available for pre-paid commitments. <span onClick={handleFooterClick} className="cursor-pointer underline">Request a demo</span> to learn more.
            </Text>
          </div>
        </div>
      </ThemeWrapper>
    </Modal>
  );
};
